/**
 * DrillsView (P132.2, #482) -- resilience drill / BCP test management.
 *
 *   - Admin create form (schedule a drill against an IBS).
 *   - Drill list, status pill, RTO/RPO target-vs-actual pill.
 *   - Per-row workflow buttons gated by status (start / pass / fail /
 *     cancel); fail captures severity + notes inline and auto-raises a
 *     Finding (link surfaced on the row).
 *
 * Twin owns operational writes (no 3-position workflow) -- admin-gated.
 */
import { canRunOperations } from "shared/api/authority";
import { Figure } from "shared/components/Figure";
import { Bdi } from "shared/components/Bdi";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useDateInputLang } from "shared/i18n/dateInputLang";

import {
  useCreateDrill,
  useDrillIbsOptions,
  useDrills,
  useTransitionDrill,
} from "twin/api/drill_hooks";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { useMe } from "shared/api/useMe";
import type { DrillStatus, ResilienceDrill } from "shared/api/types";
import { useToast } from "shared/toast/ToastProvider";

import { Arrow } from "shared/components/Arrow";
const STATUS_PILL: Record<DrillStatus, string> = {
  planned: "bg-slate-100 text-slate-700",
  in_progress: "bg-amber-100 text-amber-800",
  passed: "bg-emerald-100 text-emerald-800",
  failed: "bg-rose-100 text-rose-800",
  cancelled: "bg-slate-200 text-slate-500",
};

const DRILL_TYPES = ["bcdr", "tabletop", "failover", "chaos", "scenario"];
const SEVERITIES = ["critical", "high", "medium", "low"];

const BRASS_BTN =
  "rounded bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy " +
  "hover:bg-brand-brassDark disabled:opacity-50";
const OUTLINE_BTN =
  "rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1 text-sm " +
  "text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50";

/** RAG tone for an actual-vs-target metric (green <= target, amber
 *  within 25% over, red beyond). Pure so the test can pin it. */
export function metricTone(
  target: number | null,
  actual: number | null,
): "ok" | "warn" | "bad" | "none" {
  if (target == null || actual == null) return "none";
  if (actual <= target) return "ok";
  if (actual <= target * 1.25) return "warn";
  return "bad";
}

const TONE_CLASS: Record<string, string> = {
  ok: "text-emerald-700",
  warn: "text-amber-700",
  bad: "text-rose-700",
  none: "text-slate-500",
};

export function DrillsView() {
  const { t } = useTranslation();
  const me = useMe();
  // A8: the backend accepts head_of_opsres on schedule, create and run
  // (require_opsres_or_admin), and this hid the forms from her anyway.
  // The Resilience Lead could not schedule a drill in the product whose
  // operation is her job.
  const isAdmin = canRunOperations(me.data);
  const drillsQ = useDrills();
  const drills = drillsQ.data ?? [];

  return (
    <article data-testid="drills-view" className="mx-auto max-w-5xl space-y-6 p-4">
      <header className="space-y-1">
        <p className="font-mono text-xs uppercase tracking-wide text-slate-500">
          {t("modules.twin.drills.rubric")}
        </p>
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="drill"
            size={22}
            className="me-2 inline-block align-[-0.12em] text-brand-brassDark"
          />
          {t("modules.twin.drills.heading")}
        </h1>
        <p className="text-sm text-slate-600">{t("modules.twin.drills.intro")}</p>
      </header>

      {isAdmin && <CreateDrillForm />}

      {drillsQ.isLoading ? (
        <p className="text-sm text-slate-500">{t("modules.twin.drills.loading")}</p>
      ) : drills.length === 0 ? (
        <p data-testid="drills-empty" className="text-sm text-slate-500">
          {t("modules.twin.drills.empty")}
        </p>
      ) : (
        <ul data-testid="drills-rows" className="space-y-2">
          {drills.map((d) => (
            <DrillRow key={d.id} drill={d} isAdmin={isAdmin} />
          ))}
        </ul>
      )}
    </article>
  );
}

// ── Create (admin) ───────────────────────────────────────────────────

function CreateDrillForm() {
  const { t } = useTranslation();
  const dateInputLang = useDateInputLang();
  const toast = useToast();
  const create = useCreateDrill();
  const ibsQ = useDrillIbsOptions(true);
  const [open, setOpen] = useState(false);
  const [ibsId, setIbsId] = useState("");
  const [name, setName] = useState("");
  const [drillType, setDrillType] = useState("failover");
  const [scheduledFor, setScheduledFor] = useState("");
  const [rto, setRto] = useState("");
  const [rpo, setRpo] = useState("");

  const ready = ibsId && name.trim() && scheduledFor;

  if (!open) {
    return (
      <button
        type="button"
        data-testid="drill-new-toggle"
        className={OUTLINE_BTN}
        onClick={() => setOpen(true)}
      >
        {t("modules.twin.drills.schedule")}
      </button>
    );
  }

  return (
    <form
      data-testid="drill-new-form"
      className="space-y-3 rounded-lg border border-slate-200 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready) return;
        create.mutate(
          {
            ibs_service_id: ibsId,
            name: name.trim(),
            drill_type: drillType as never,
            scheduled_for: scheduledFor,
            target_rto_minutes: rto ? Number(rto) : null,
            target_rpo_minutes: rpo ? Number(rpo) : null,
          },
          {
            onSuccess: () => {
              toast.push({
                tone: "success",
                message: t("modules.twin.drills.scheduled"),
              });
              setOpen(false);
              setName("");
              setIbsId("");
            },
            onError: (e) => toast.push({ tone: "error", message: e.detail ?? "error" }),
          },
        );
      }}
    >
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs text-slate-500">
          {t("modules.twin.drills.ibs")}
          <select
            data-testid="drill-ibs"
            className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm"
            value={ibsId}
            onChange={(e) => setIbsId(e.target.value)}
          >
            <option value="">{t("modules.twin.drills.selectIbs")}</option>
            <optgroup label={t("modules.twin.drills.servicesGroup")}>
              {(ibsQ.data ?? [])
                .filter((o) => o.service_class !== "internal")
                .map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
            </optgroup>
            {/* Round 16 L4 -- a function rehearsed under its own heading. */}
            {(ibsQ.data ?? []).some((o) => o.service_class === "internal") && (
              <optgroup label={t("modules.twin.drills.functionsGroup")}>
                {(ibsQ.data ?? [])
                  .filter((o) => o.service_class === "internal")
                  .map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
              </optgroup>
            )}
          </select>
        </label>
        <label className="text-xs text-slate-500">
          {t("modules.twin.drills.type")}
          <select
            data-testid="drill-type"
            className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm"
            value={drillType}
            onChange={(e) => setDrillType(e.target.value)}
          >
            {DRILL_TYPES.map((dt) => (
              <option key={dt} value={dt}>
                {t(`modules.twin.drills.types.${dt}`)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {/* Round 10 F2 -- a placeholder is not an accessible name, and
          it disappears the moment somebody types into the box. */}
      <input
        dir="auto"
        aria-label={t("modules.twin.drills.namePlaceholder")}
        data-testid="drill-name"
        className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
        placeholder={t("modules.twin.drills.namePlaceholder")}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <div className="grid grid-cols-3 gap-2">
        <label className="text-xs text-slate-500">
          {t("modules.twin.drills.scheduledFor")}
          <input
            type="date"
            lang={dateInputLang}
            className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-sm"
            value={scheduledFor}
            onChange={(e) => setScheduledFor(e.target.value)}
          />
        </label>
        <label className="text-xs text-slate-500">
          {t("modules.twin.drills.targetRto")}
          <input
            type="number"
            min={0}
            /* Round 16 H23, eleventh round -- named outright; the wrapping
               label alone was not read as a name by every instrument. */
            aria-label={t("modules.twin.drills.targetRto")}
            data-testid="drill-target-rto"
            className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-sm"
            value={rto}
            onChange={(e) => setRto(e.target.value)}
          />
        </label>
        <label className="text-xs text-slate-500">
          {t("modules.twin.drills.targetRpo")}
          <input
            type="number"
            min={0}
            /* Round 16 H23, eleventh round -- named outright; the wrapping
               label alone was not read as a name by every instrument. */
            aria-label={t("modules.twin.drills.targetRpo")}
            data-testid="drill-target-rpo"
            className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-sm"
            value={rpo}
            onChange={(e) => setRpo(e.target.value)}
          />
        </label>
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          className={BRASS_BTN}
          disabled={!ready || create.isPending}
        >
          {t("modules.twin.drills.create")}
        </button>
        <button type="button" className={OUTLINE_BTN} onClick={() => setOpen(false)}>
          {t("modules.twin.drills.cancel")}
        </button>
      </div>
    </form>
  );
}

// ── Row ──────────────────────────────────────────────────────────────

function DrillRow({ drill, isAdmin }: { drill: ResilienceDrill; isAdmin: boolean }) {
  const { t } = useTranslation();
  const toast = useToast();
  const transition = useTransitionDrill();
  const [expanded, setExpanded] = useState(false);
  const [failing, setFailing] = useState(false);
  const [severity, setSeverity] = useState("high");
  const [notes, setNotes] = useState("");

  function act(action: "start" | "pass" | "fail" | "cancel") {
    transition.mutate(
      {
        id: drill.id,
        action,
        ...(action === "fail"
          ? { severity, ...(notes ? { outcomeNotes: notes } : {}) }
          : {}),
      },
      {
        onSuccess: () => {
          toast.push({
            tone: action === "fail" ? "warning" : "success",
            message: t(`modules.twin.drills.acted.${action}`),
          });
          setFailing(false);
          setNotes("");
        },
        onError: (e) => toast.push({ tone: "error", message: e.detail ?? "error" }),
      },
    );
  }

  const rtoTone = metricTone(drill.target_rto_minutes, drill.actual_rto_minutes);

  return (
    <li
      data-testid={`drill-row-${drill.id}`}
      className="rounded border border-slate-200 p-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          className="text-start"
          onClick={() => setExpanded((v) => !v)}
        >
          <span className="text-sm font-medium text-slate-800">{drill.name}</span>
          {/* Round 12 H5 -- a margin drew the gaps and put nothing in
              the text, so the row read "Q2 failover drillPlannedRetail
              payments" to a copy, a screen reader and an export. */}
          {" \u00b7 "}
          <span
            className={`ms-2 rounded px-2 py-0.5 text-xs ${STATUS_PILL[drill.status]}`}
            data-testid={`drill-status-${drill.id}`}
          >
            {t(`modules.twin.drills.status.${drill.status}`)}
          </span>
          {" \u00b7 "}
          <span className="ms-2 text-xs text-slate-500">
            {/* D2 -- a name, a translated type and a date stitched with
                middle dots. Reordered, the date led and the name went
                to the far end. <Bdi> for the name (it takes its own
                direction) and <Figure> for the date (a date is always
                left to right). */}
            <Bdi>{drill.ibs_name ?? drill.ibs_service_id}</Bdi> ·{" "}
            {t(`modules.twin.drills.types.${drill.drill_type}`)} ·{" "}
            <Figure>{drill.scheduled_for}</Figure>
          </span>
          {/* Round 20 V3 -- target, actual and tolerance on the row, not
              only in the detail: a "Passed" at 300 minutes against 120
              read as a pass until the drill was opened. */}
          {(drill.target_rto_minutes != null || drill.actual_rto_minutes != null) && (
            <span
              data-testid={`drill-figures-${drill.id}`}
              className="ms-2 block text-xs text-slate-500 sm:inline"
            >
              {t("modules.twin.drills.rowFigures", {
                target: drill.target_rto_minutes ?? "—",
                actual: drill.actual_rto_minutes ?? "—",
              })}
              {drill.tolerance_minutes != null &&
                ` · ${t("modules.twin.drills.rowTolerance", { minutes: drill.tolerance_minutes })}`}
            </span>
          )}
          {/* Round 20 Y7 -- a target above the signed tolerance is
              flagged, and the actual is read against both. */}
          {drill.target_above_tolerance_minutes != null && (
            <span
              data-testid={`drill-target-above-${drill.id}`}
              className="ms-2 block text-xs font-medium text-rag-amber"
            >
              {t("modules.twin.drills.targetAboveTolerance", {
                minutes: drill.target_above_tolerance_minutes,
              })}
            </span>
          )}
          {drill.against_target && drill.against_tolerance && (
            <span
              data-testid={`drill-against-both-${drill.id}`}
              className="ms-2 block text-xs text-slate-600"
            >
              {t("modules.twin.drills.againstBoth", {
                target: t(`modules.twin.drills.againstWord.${drill.against_target}`),
                tolerance: t(
                  `modules.twin.drills.againstWord.${drill.against_tolerance}`,
                ),
              })}
            </span>
          )}
          {drill.pass_contradiction && (
            <span
              data-testid={`drill-contradiction-${drill.id}`}
              className="ms-2 block text-xs font-medium text-rose-700"
            >
              {t(`modules.twin.drills.contradiction.${drill.pass_contradiction}`, {
                minutes: drill.pass_contradicted_by_minutes ?? 0,
              })}
            </span>
          )}
        </button>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            {drill.status === "planned" && (
              <button
                type="button"
                data-testid={`drill-start-${drill.id}`}
                className={BRASS_BTN}
                onClick={() => act("start")}
              >
                {t("modules.twin.drills.start")}
              </button>
            )}
            {drill.status === "in_progress" && (
              <>
                <button
                  type="button"
                  data-testid={`drill-pass-${drill.id}`}
                  className={BRASS_BTN}
                  onClick={() => act("pass")}
                >
                  {t("modules.twin.drills.pass")}
                </button>
                <button
                  type="button"
                  data-testid={`drill-fail-${drill.id}`}
                  className={OUTLINE_BTN}
                  onClick={() => setFailing((v) => !v)}
                >
                  {t("modules.twin.drills.fail")}
                </button>
              </>
            )}
            {(drill.status === "planned" || drill.status === "in_progress") && (
              <button
                type="button"
                data-testid={`drill-cancel-${drill.id}`}
                className={OUTLINE_BTN}
                onClick={() => act("cancel")}
              >
                {t("modules.twin.drills.cancelDrill")}
              </button>
            )}
          </div>
        )}
      </div>

      {failing && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select
            aria-label={t("modules.twin.drills.failSeverityLabel")}
            data-testid={`drill-fail-severity-${drill.id}`}
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
          >
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <input
            dir="auto"
            aria-label={t("modules.twin.drills.failNotesPlaceholder")}
            className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
            placeholder={t("modules.twin.drills.failNotesPlaceholder")}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <button type="button" className={BRASS_BTN} onClick={() => act("fail")}>
            {t("modules.twin.drills.confirmFail")}
          </button>
        </div>
      )}

      {expanded && (
        <dl
          data-testid={`drill-detail-${drill.id}`}
          className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-xs text-slate-600"
        >
          {drill.description && <p>{drill.description}</p>}
          <p className={TONE_CLASS[rtoTone]} data-testid={`drill-rto-${drill.id}`}>
            {t("modules.twin.drills.rto")}: {drill.target_rto_minutes ?? "—"} <Arrow />{" "}
            {drill.actual_rto_minutes ?? "—"} {t("modules.twin.drills.minutes")}
          </p>
          <p>
            {t("modules.twin.drills.rpo")}: {drill.target_rpo_minutes ?? "—"} <Arrow />{" "}
            {drill.actual_rpo_minutes ?? "—"} {t("modules.twin.drills.minutes")}
          </p>
          {drill.outcome_notes && (
            <p className="text-slate-700">
              {t("modules.twin.drills.outcome")}: {drill.outcome_notes}
            </p>
          )}
          {drill.raised_finding_id && (
            <p>
              <Link
                data-testid={`drill-finding-link-${drill.id}`}
                className="text-brand-brassDark hover:underline"
                to={`/twin/findings/${drill.raised_finding_id}`}
              >
                {t("modules.twin.drills.viewFinding")}
              </Link>
            </p>
          )}
        </dl>
      )}
    </li>
  );
}
